import { useState, useEffect, useCallback } from 'react';
import { getFromStorage, saveToStorage } from '../utils/localStorage.js';
import { getUILoc, getCardName } from '../utils/cardUtils.js';

// 初期デッキの定義 (名前と説明はKeyで管理、カードはIDで管理)
export const INITIAL_DECKS = {
  AGRI: {
    nameKey: 'YONOTSUNE.INITIAL_DECKS.AGRI.NAME',
    descriptionKey: 'YONOTSUNE.INITIAL_DECKS.AGRI.DESC',
    cards: [7, 7, 6, 70, 10, 10, 10, 11],
    memoryEffect: {
      key: 'YONOTSUNE.MEMORY_EFFECTS.AGRI',
      type: 'FRICTION_EXCEPT',
      con: 3, scale: 3
    }
  },
  TRIBE: {
    nameKey: 'YONOTSUNE.INITIAL_DECKS.TRIBE.NAME',
    descriptionKey: 'YONOTSUNE.INITIAL_DECKS.TRIBE.DESC',
    cards: [1, 1, 7, 10, 15, 56, 56, 75],
    memoryEffect: {
      key: 'YONOTSUNE.MEMORY_EFFECTS.TRIBE',
      type: 'MATCH_WIN',
      con: 3, scale: 5
    }
  },
  UTOPIA: {
    nameKey: 'YONOTSUNE.INITIAL_DECKS.UTOPIA.NAME',
    descriptionKey: 'YONOTSUNE.INITIAL_DECKS.UTOPIA.DESC',
    cards: [6, 6, 10, 10, 9, 21, 48, 22],
    memoryEffect: {
      key: 'YONOTSUNE.MEMORY_EFFECTS.UTOPIA',
      type: 'CARD_ADDED',
      con: 1
    }
  },
  TREASURE: {
    nameKey: 'YONOTSUNE.INITIAL_DECKS.TREASURE.NAME',
    descriptionKey: 'YONOTSUNE.INITIAL_DECKS.TREASURE.DESC',
    cards: [33, 7, 10, 10, 8, 8, 17, 21],
    memoryEffect: {
      key: 'YONOTSUNE.MEMORY_EFFECTS.TREASURE',
      type: 'SCALE_INCREASED'
    }
  }
};

// 最終ボスデッキの定義
export const BOSS_DECKS = {
  EMPIRE: {
    nameKey: 'YONOTSUNE.BOSS_DECKS.EMPIRE',
    cards: [  // 入れたいカード：開拓民(8)、軍国主義(77)、戦士(1)、帝国主義(20)、覇権主義(82)、内部崩壊(13)、確証破壊能力(89)
      1, 8, 8, 8, 8, 77,
      1, 1, 89, 1, 77, 1,
      1, 1, 1, 77, 20, 13,
      1, 20, 1, 82, 1, 13,
      1, 13, 13, 13, 13, 13
    ],
    consciousness: 45,
    scale: 15,
    bgmPack: 'empire'  // BGMパックを指定
  },
  MONOTHEISM: {
    nameKey: 'YONOTSUNE.BOSS_DECKS.MONOTHEISM',
    cards: [  // 入れたいカード：覚醒(12 / 1枚)、一神教(24)、布教(27)、風害(9)、焦土(39)、占領(40)、教育機関(93)、思想の弾圧(60)、官僚主義(69)、文化侵略(95)、ディアスポラ(44)
      12, 
      69, 93, 93, 9, 60,
      40, 60, 60, 60, 60,
      44, 95, 27, 27, 27,
      27, 44, 27, 60, 27,
      27, 27, 60, 27, 95,
      60, 95, 60, 24
    ],
    consciousness: 85,
    scale: 5,
    bgmPack: 'monotheism'  // BGMパックを指定
  },
  COMMUNITY: {
    nameKey: 'YONOTSUNE.BOSS_DECKS.COMMUNITY',
    cards: [  // 入れたいカード：農民(7)、豊作(10)、環境主義(71)、商人(42)、災害多発地域(68)、孤立主義(78)、教育機関(93)、不干渉主義(63)、共同体主義(58 / 1枚最後のほう)
      7, 42, 71, 10, 42, 42, 42, 10, 10,
      10, 10, 78, 42, 63, 10, 93, 96, 10,
      10, 68, 93, 63, 10, 42, 10, 68, 10, 
      58, 10, 10
    ],
    consciousness: 50,
    scale: 15,
    bgmPack: 'community'  // BGMパックを指定
  }
};

// 外交交渉決裂戦用NPCデッキ
export const DIPLOMACY_BREAKDOWN_DECK = {
  nameKey: 'YONOTSUNE.DIPLOMACY_BREAKDOWN_DECK',
  cards: [1, 50, 1, 75, 1, 50, 1, 50, 1, 50, 1, 50, 1, 50, 13, 13, 13, 13], // 入れたいカード：内部崩壊(13)、兵器(50)、戦士(1)、結束主義(75)
  consciousness: 12,
  scale: 7,
  bgmPack: 'breakdown'  // BGMパックを指定
};

// 対話の秋専用固定NPCデッキ
export const OPPORTUNITY_DECK = {
  nameKey: 'YONOTSUNE.OPPORTUNITY_DECK',
  cards: [10, 12, 12, 12, 12, 9, 4, 4, 69, 86, 9, 69, 4, 3, 69, 69, 4, 4, 69, 69, 9, 3, 3, 9, 17, 17, 70, 81, 60, 53, 53, 7, 60, 7, 7, 7, 7, 7, 7, 7], // 入れたいカード：豊作(10)、覚醒(12)、強制収用(17)、共産主義(81)、保守主義(70)、官僚主義(69)、砦(3)、聖なる領域(4)、風害(9)、思想の弾圧(60)、啓蒙時代(53)、自由主義(86)、農民(7)
  consciousness: 25,
  scale: 2,
  bgmPack: 'game'
};

// 進行イベントの定義
export const PROCEEDING_TYPES = {
  FRICTION: 'FRICTION',
  WHIM: 'WHIM',
  CRYSTAL: 'CRYSTAL',
  SHARPEN: 'SHARPEN',
  DIPLOMACY: 'DIPLOMACY',
  OPPORTUNITY: 'OPPORTUNITY'
};

export const MAX_PROCEEDINGS = 10;

const PROCEEDING_POOL = [
  { id: PROCEEDING_TYPES.FRICTION, weight: 4 },
  { id: PROCEEDING_TYPES.WHIM, weight: 2 },
  { id: PROCEEDING_TYPES.CRYSTAL, weight: 1 },
  { id: PROCEEDING_TYPES.SHARPEN, weight: 1 },
  { id: PROCEEDING_TYPES.DIPLOMACY, weight: 1 },
  { id: PROCEEDING_TYPES.OPPORTUNITY, weight: 1 }
];

const LOCAL_STORAGE_KEY = 'historio_yonotsune_active_run';
const ACHIEVEMENTS_KEY = 'historio_yonotsune_achievements';
const RECORDS_STORAGE_KEY = 'historio_yonotsune_records';

const DEFAULT_RECORDS = {
  totalRuns: 0,
  clearsByMemory: {
    AGRI: 0,
    TRIBE: 0,
    UTOPIA: 0,
    TREASURE: 0
  },
  phaseLossCounts: {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0
  },
  maxBreakoutVictories: 0
};

export const useYonotsune = (cardDefs) => {
  const [activeRun, setActiveRun] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [toast, setToast] = useState(null);
  const [records, setRecords] = useState(() => {
    return getFromStorage(RECORDS_STORAGE_KEY) || DEFAULT_RECORDS;
  });

  // ヘルパー: デッキへのカード追加
  const addCardToDeck = useCallback((run, cardId, isAcquiredCardsCount = true) => {
    run.playerDeck.push(cardId);
    // 累計獲得枚数をカウント
    if (isAcquiredCardsCount) {
        run.acquiredCardsCount = (run.acquiredCardsCount || 0) + 1;
        
      // デッキへのカード追加時の記憶効果
      if (INITIAL_DECKS[run.initialDeckKey]?.memoryEffect?.type === 'CARD_ADDED') {
        run.consciousnessBonus += INITIAL_DECKS[run.initialDeckKey].memoryEffect.con || 0;
        run.scaleBonus += INITIAL_DECKS[run.initialDeckKey].memoryEffect.scale || 0;
      }
    }
  }, []);

  // 実績のロード
  useEffect(() => {
    const savedAchievements = getFromStorage(ACHIEVEMENTS_KEY) || [];
    setAchievements(savedAchievements);
  }, []);

  // 実績の全リセット (デバッグ用)
  const resetAchievements = useCallback(() => {
    setAchievements([]);
    saveToStorage(ACHIEVEMENTS_KEY, []);
  }, []);

  // アクティブなランのロード
  useEffect(() => {
    const savedRun = getFromStorage(LOCAL_STORAGE_KEY);
    if (savedRun && savedRun.isActive && cardDefs) {
      const run = savedRun.currentRun;

      // 互換性マイグレーション1: 名前(String)をID(Number)に変換
      if (run.playerDeck && run.playerDeck.length > 0 && typeof run.playerDeck[0] === 'string') {
        console.log('[YONOTSUNE] Migrating player deck names to IDs...');
        run.playerDeck = run.playerDeck.map(idOrName => {
          if (typeof idOrName === 'number') return idOrName;
          const def = Object.values(cardDefs).find(d => d.name === idOrName);
          return def ? def.id : idOrName;
        });
      }

      // 互換性マイグレーション2: bossKeyがない場合は移行を試みる
      if (!run.bossKey && run.bossDeckName) {
        console.log('[YONOTSUNE] Migrating bossDeckName to bossKey...');
        if (run.bossDeckName.includes('帝国') || run.bossDeckName.includes('Empire')) run.bossKey = 'EMPIRE';
        else if (run.bossDeckName.includes('一神') || run.bossDeckName.includes('God')) run.bossKey = 'MONOTHEISM';
        else if (run.bossDeckName.includes('共同体') || run.bossDeckName.includes('Community')) run.bossKey = 'COMMUNITY';
        else run.bossKey = 'EMPIRE'; // Fallback
      }

      // 互換性マイグレーション3: selectedDeckName が多言語化されていない場合の補正
      // (新しいランでは startNewRun 内で翻訳キーを解決した後の文字列が入るが、古いランでは日本語固定の可能性がある)

      setActiveRun(run);
    }
  }, [cardDefs]);

  // 実績解除処理
  const unlockAchievement = useCallback((id) => {
    setAchievements((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      saveToStorage(ACHIEVEMENTS_KEY, next);

      // トースト表示
      const name = getUILoc(`YONOTSUNE.ACHIEVEMENTS_SCREEN.LIST.${id}.NAME`);
      const desc = getUILoc(`YONOTSUNE.ACHIEVEMENTS_SCREEN.LIST.${id}.DESC`);
      setToast({ id, name, desc });
      setTimeout(() => setToast(null), 4000);

      // 効果音を鳴らす（audioManagerがあれば）
      try {
        if (window.audioManager) {
          window.audioManager.playSE('place');
        } else {
          const selectAudio = document.getElementById('se-place');
          if (selectAudio) selectAudio.play().catch(() => {});
        }
      } catch (e) {}

      return next;
    });
  }, []);

  // 実績エクスポート
  const exportAchievements = useCallback(() => {
    const data = {
      achievements,
      exportTime: Date.now(),
      sign: 'historio_yonotsune'
    };
    return btoa(unescape(encodeURIComponent(JSON.stringify(data))));
  }, [achievements]);

  // 実績インポート
  const importAchievements = useCallback((base64Str) => {
    try {
      const jsonStr = decodeURIComponent(escape(atob(base64Str)));
      const data = JSON.parse(jsonStr);
      if (data.sign === 'historio_yonotsune' && Array.isArray(data.achievements)) {
        setAchievements(data.achievements);
        saveToStorage(ACHIEVEMENTS_KEY, data.achievements);
        return true;
      }
    } catch (e) {
      console.error('Failed to import achievements', e);
    }
    return false;
  }, []);

  // 非トークンかつNPCが使えるカードプール
  const getNPCCardPool = useCallback(() => {
    if (!cardDefs) return [];
    const blacklist = [88, 24, 58]; // ポピュリズム, 一神教, 共同体主義のID
    return Object.values(cardDefs).filter(def => !def.is_token && !blacklist.includes(def.id) && def.is_enabled !== false);
  }, [cardDefs]);

  // 対戦終了後の報酬ドラフト処理
  const generateDraftOptions = useCallback((enemyDeckCardIds, isBreakdown = false) => {
    // enemyDeckCardIdsが空でもnullを返さず、全プールから生成するように変更
    const pool = (enemyDeckCardIds && enemyDeckCardIds.length > 0) ? [...enemyDeckCardIds] : [];
    const allNPCCards = getNPCCardPool();
    
    const chosen = [];
    for (let i = 0; i < 6; i++) {
      if (pool.length > 0) {
        const randIdx = Math.floor(Math.random() * pool.length);
        chosen.push(pool[randIdx]);
      } else {
        if (allNPCCards.length > 0) {
          chosen.push(allNPCCards[Math.floor(Math.random() * allNPCCards.length)].id);
        }
      }
    }

    const pattern1 = [chosen[0], chosen[1], chosen[2]];
    const pattern2 = [chosen[3], chosen[4], chosen[5]];

    if (isBreakdown) {
      // 交渉決裂戦勝利時は「確証破壊能力」(ID 89)を確定で入れる
      pattern2[2] = 89;
    } else {
      // ワイルドカード pool: [農民(7), マネー(33), 開拓民(8)]
      const wildPool = [7, 33, 8];
      pattern2[2] = wildPool[Math.floor(Math.random() * wildPool.length)];
    }

    return { pattern1, pattern2 };
  }, [getNPCCardPool]);

  // ヘルパー: 規模ボーナスの変更と記憶効果（財宝と名声）の処理
  const modifyScaleBonus = useCallback((run, delta) => {
    if (delta <= 0) {
      run.scaleBonus += delta;
      if (run.scaleBonus < -5) run.scaleBonus = -5;
      return;
    }
    run.scaleBonus += delta;
    if (INITIAL_DECKS[run.initialDeckKey]?.memoryEffect?.type === 'SCALE_INCREASED') {
      run.consciousnessBonus += delta;
      const pool = getNPCCardPool();
      if (pool.length > 0) {
        const randomCard = pool[Math.floor(Math.random() * pool.length)];
        addCardToDeck(run, randomCard.id);
      }
    }
  }, [getNPCCardPool, addCardToDeck]);

  const generateBuffOptions = useCallback((phase, isBreakdown = false, isOpportunity = false) => {
    if (isOpportunity) {
      return [
        { id: 'control', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.CONTROL'), effect: { con: 20, scale: -5 } },
        { id: 'domination', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.DOMINATION'), effect: { con: 12, scale: -1 } },
        { id: 'absorption', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.ABSORPTION'), effect: { con: 0, scale: 15 } }
      ];
    }

    if (isBreakdown) {
      // 交渉決裂戦勝利時は特別バフ「熱狂」固定
      return [
        { 
          id: 'enthusiasm', 
          desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.ENTHUSIASM'), 
          effect: { con: 5, scale: 10 } 
        }
      ];
    }

    const baseOptions = [
      { id: 'con_2', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.CON_2'), effect: { con: 2, scale: 0 } },
      { id: 'scale_2', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.SCALE_2'), effect: { con: 0, scale: 2 } },
      { id: 'both_1', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.BOTH_1'), effect: { con: 1, scale: 1 } }
    ];

    if (phase >= 3) {
      baseOptions.push(
        { id: 'con_minus1_scale_4', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.CON_M1_S4'), effect: { con: -1, scale: 4 } },
        { id: 'con_4_scale_minus1', desc: getUILoc('YONOTSUNE.BUFF_OPTIONS.CON_4_S_M1'), effect: { con: 4, scale: -1 } }
      );
    }

    const chosen = [];
    const pool = [...baseOptions];
    for (let i = 0; i < 3; i++) {
      if (pool.length > 0) {
        const idx = Math.floor(Math.random() * pool.length);
        chosen.push(pool.splice(idx, 1)[0]);
      }
    }

    if (phase === 4) {
      const specialIdeologyIds = [88, 24, 58]; 
      const chosenIdeologyId = specialIdeologyIds[Math.floor(Math.random() * specialIdeologyIds.length)];
      const chosenIdeologyName = getCardName(chosenIdeologyId) || getUILoc('YONOTSUNE.BUFF_SPECIAL_DEFAULT');

      chosen[2] = {
        id: 'special_phase4',
        effect: { con: -1, scale: -1, addCardId: chosenIdeologyId },
        desc: getUILoc('YONOTSUNE.BUFF_SPECIAL_DESC', { name: chosenIdeologyName })
      };
    }

    return chosen;
  }, []);

  // 進行イベントの抽選ロジック
  const generateProceedingNodes = useCallback(() => {
    const count = Math.floor(Math.random() * 3) + 2; // 2, 3, 4

    const weightPool = [];
    PROCEEDING_POOL.forEach(node => {
        const nodeInfo = {
            id: node.id,
            name: getUILoc(`YONOTSUNE.EVENTS.${node.id}.NAME`),
            desc: getUILoc(`YONOTSUNE.EVENTS.${node.id}.DESC`)
        };
      for (let i = 0; i < node.weight; i++) {
        weightPool.push(nodeInfo);
      }
    });

    let chosen = [];
    for (let i = 0; i < count; i++) {
      const idx = Math.floor(Math.random() * weightPool.length);
      chosen.push(weightPool[idx]);
    }

    let uniqueMap = new Map();
    chosen.forEach(item => uniqueMap.set(item.id, item));
    let result = Array.from(uniqueMap.values());

    if (result.length === 1) {
      let retryPool = weightPool.filter(item => item.id !== result[0].id);
      if (retryPool.length > 0) {
        const idx = Math.floor(Math.random() * retryPool.length);
        result.push(retryPool[idx]);
      }
    }

    return result;
  }, []);

  // セーブ処理
  const saveRun = useCallback((runData) => {
    setActiveRun(runData);
    saveToStorage(LOCAL_STORAGE_KEY, {
      isActive: true,
      currentRun: runData
    });
  }, []);

  // 対局終了時のリザルト記録（勝敗と報酬の選択肢を固定・保存する）
  const recordMatchResult = useCallback((isVictory, enemyPool) => {
    setActiveRun(prev => {
        if (!prev) return null;
        const nextRun = deepClone(prev);
        const isBreakdown = nextRun.isDiplomacyBreakdown;
        const isOpportunity = nextRun.isOpportunityBattle;
        
        if (isVictory && isBreakdown) {
            nextRun.diplomacyWins = (nextRun.diplomacyWins || 0) + 1;
            setRecords(r => {
                const cur = r || DEFAULT_RECORDS;
                const newMax = Math.max(cur.maxBreakoutVictories || 0, nextRun.diplomacyWins);
                const next = { ...cur, maxBreakoutVictories: newMax };
                saveToStorage(RECORDS_STORAGE_KEY, next);
                return next;
            });
        }
        
        const result = {
            isVictory,
            draftOptions: isVictory ? generateDraftOptions(enemyPool, isBreakdown) : null,
            buffOptions: isVictory ? generateBuffOptions(nextRun.currentPhase, isBreakdown, isOpportunity) : null
        };
        
        nextRun.pendingMatchResult = result;
        
        saveToStorage(LOCAL_STORAGE_KEY, {
            isActive: true,
            currentRun: nextRun
        });
        return nextRun;
    });
  }, [generateDraftOptions, generateBuffOptions]);

  // 敗北時のリセット
  const resetRun = useCallback(() => {
    setActiveRun(null);
    saveToStorage(LOCAL_STORAGE_KEY, {
      isActive: false,
      currentRun: null
    });
  }, []);

  // ランの開始
  const startNewRun = useCallback((deckKey) => {
    const initialDeckConfig = INITIAL_DECKS[deckKey];
    if (!initialDeckConfig) return;

    setRecords(r => {
      const cur = r || DEFAULT_RECORDS;
      const next = {
        ...cur,
        totalRuns: (cur.totalRuns || 0) + 1
      };
      saveToStorage(RECORDS_STORAGE_KEY, next);
      return next;
    });

    const bossKeys = Object.keys(BOSS_DECKS);
    const chosenBossKey = bossKeys[Math.floor(Math.random() * bossKeys.length)];

    const newRun = {
      initialDeckKey: deckKey,
      playerDeck: [],
      matchedCount: 0,
      proceedingCount: 0,
      consciousnessBonus: 0,
      scaleBonus: 0,
      acquiredCardsCount: 0,
      frictionWins: 0,
      history: [],
      currentPhase: 1,
      bossKey: chosenBossKey,
      currentNodes: [],
      activeNode: null,
      eventState: {},
      isVictory: false,
      isDiplomacyBreakdown: false,
      isOpportunityBattle: false,
      diplomacyWins: 0,
      pendingMatchResult: null
    };

    initialDeckConfig.cards.forEach(cardId => addCardToDeck(newRun, cardId, false));

    const frictionNode = {
        id: PROCEEDING_TYPES.FRICTION,
        name: getUILoc('YONOTSUNE.EVENTS.FRICTION.NAME'),
        desc: getUILoc('YONOTSUNE.EVENTS.FRICTION.DESC')
    };
    newRun.currentNodes = [frictionNode];
    newRun.activeNode = PROCEEDING_TYPES.FRICTION;

    saveRun(newRun);
  }, [saveRun, addCardToDeck]);

  // 次のノード群を生成して待機
  const goToNextNodeSelection = useCallback(() => {
    setActiveRun(prev => {
        if (!prev) return null;
        const nextRun = deepClone(prev);
        nextRun.activeNode = null;
        nextRun.eventState = {};
        nextRun.isDiplomacyBreakdown = false; // 強制リセット
        nextRun.isOpportunityBattle = false; // 強制リセット
        nextRun.pendingMatchResult = null; // 報酬適用済みなのでクリア

        const matched = nextRun.matchedCount;

        // 5進行完了時にボス開示フラグを立てる (1度のみ)
        if (matched === 5 && !nextRun.hasRevealedBoss) {
            nextRun.showBossReveal = true;
            nextRun.hasRevealedBoss = true;
        }

        if (matched === 1) nextRun.currentPhase = 2;
        else if (matched >= 2 && matched <= 3) nextRun.currentPhase = 3;
        else if (matched >= 4 && matched <= 6) nextRun.currentPhase = 4;
        else if (matched >= 7 && matched <= 8) nextRun.currentPhase = 5;
        else if (matched >= 9) nextRun.currentPhase = 6;

        if (matched === 9) {
            const frictionNode = {
                id: PROCEEDING_TYPES.FRICTION,
                name: getUILoc('YONOTSUNE.EVENTS.FRICTION.NAME'),
                desc: getUILoc('YONOTSUNE.EVENTS.FRICTION.DESC')
            };
            nextRun.currentNodes = [frictionNode];
        } else {
            nextRun.currentNodes = generateProceedingNodes();
        }

        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
  }, [generateProceedingNodes]);

  // 進行イベントの選択
  const selectNode = useCallback((nodeId) => {
    setActiveRun(prev => {
        if (!prev) return null;
        const nextRun = deepClone(prev);
        
        // 非「文化摩擦」時の記憶効果 (AGRI)
        if (INITIAL_DECKS[nextRun.initialDeckKey]?.memoryEffect?.type === 'FRICTION_EXCEPT' && nodeId !== PROCEEDING_TYPES.FRICTION) {
            const effect = INITIAL_DECKS[nextRun.initialDeckKey].memoryEffect;
            nextRun.consciousnessBonus += effect.con || 0;
            modifyScaleBonus(nextRun, effect.scale || 0);
        }

        nextRun.activeNode = nodeId;
        // 進行数をここで加算するように変更
        nextRun.matchedCount += 1;
        nextRun.history.push(nodeId);

        if (nodeId === PROCEEDING_TYPES.SHARPEN) {
            // 排除最大5回
            nextRun.eventState = { remaining: 5, removedCount: 0 };
        } else if (nodeId === PROCEEDING_TYPES.DIPLOMACY) {
            // 提示カード6枚を抽選 (非トークンから)
            const pool = getNPCCardPool();
            const chosenCardIds = [];
            for (let i = 0; i < 6; i++) {
                if (pool.length > 0) {
                    const card = pool[Math.floor(Math.random() * pool.length)];
                    chosenCardIds.push(card.id);
                }
            }
            nextRun.eventState = {
                presented: chosenCardIds,
                completed: false
            };
        } else if (nodeId === PROCEEDING_TYPES.WHIM) {
            nextRun.eventState = { completed: false };
        } else if (nodeId === PROCEEDING_TYPES.OPPORTUNITY) {
            nextRun.eventState = { completed: false };
        }

        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
  }, [getNPCCardPool, modifyScaleBonus]);

  // ボス開示を閉じる
  const dismissBossReveal = useCallback(() => {
    setActiveRun(prev => {
        if (!prev) return null;
        const nextRun = deepClone(prev);
        nextRun.showBossReveal = false;
        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
  }, []);

  // A. 俯瞰者の気紛れ: カード交換
  const executeWhimExchange = useCallback((playerCardId) => {
    let result = { success: false };
    setActiveRun(prev => {
        if (!prev || prev.activeNode !== PROCEEDING_TYPES.WHIM) return prev;

        const totalScale = 5 + prev.scaleBonus;
        if (totalScale <= 0) {
            result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.SCALE_INSUFFICIENT') };
            return prev;
        }

        const pool = getNPCCardPool();
        if (pool.length === 0) {
            result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.POOL_EMPTY') };
            return prev;
        }

        const sourceCard = cardDefs[playerCardId];
        if (!sourceCard) {
            result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.CARD_NOT_FOUND') };
            return prev;
        }

        let validPool = pool;
        if (sourceCard.required_scale < 30) {
            validPool = pool.filter(card => card.card_type === sourceCard.card_type);
        }
        if (validPool.length === 0) {
            validPool = pool;
        }

        const targetCard = validPool[Math.floor(Math.random() * validPool.length)];
        const nextRun = deepClone(prev);
        const idx = nextRun.playerDeck.indexOf(playerCardId);
        if (idx !== -1) {
            nextRun.playerDeck.splice(idx, 1);
            addCardToDeck(nextRun, targetCard.id);
        }

        modifyScaleBonus(nextRun, -1);

        result = { success: true, newCardId: targetCard.id };
        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
    return result;
  }, [getNPCCardPool, cardDefs, addCardToDeck, modifyScaleBonus]);

  // B. 人文の結晶: 即時適用
  const executeCrystal = useCallback(() => {
    setActiveRun(prev => {
        if (!prev || prev.activeNode !== PROCEEDING_TYPES.CRYSTAL) return prev;
        const nextRun = deepClone(prev);
        // 芸術(ID 1000)を3枚加える
        [1000, 1000, 1000].forEach(id => addCardToDeck(nextRun, id));
        nextRun.consciousnessBonus += 1;

        const artCount = nextRun.playerDeck.filter(id => id === 1000).length;
        if (artCount >= 10) {
            unlockAchievement('TRUE_HUMANISM');
        }

        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
    goToNextNodeSelection();
  }, [goToNextNodeSelection, unlockAchievement, addCardToDeck]);

  // B'. 人文の結晶: 即時適用
  const hesitateCrystal = useCallback(() => {
    setActiveRun(prev => {
        if (!prev || prev.activeNode !== PROCEEDING_TYPES.CRYSTAL) return prev;
        const nextRun = deepClone(prev);
        // 芸術(ID 1000)を1枚加える
        [1000].forEach(id => addCardToDeck(nextRun, id));
        nextRun.matchedCount -= 1;

        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
    goToNextNodeSelection();
  }, [goToNextNodeSelection, addCardToDeck]);

  // C. 文明を研ぐ: カード排除
  const executeSharpenRemove = useCallback((cardId) => {
    let result = { success: false };
    setActiveRun(prev => {
        if (!prev || prev.activeNode !== PROCEEDING_TYPES.SHARPEN) return prev;
        if (prev.eventState.remaining <= 0) {
            result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.LIMIT_REACHED') };
            return prev;
        }

        const nextRun = deepClone(prev);
        const idx = nextRun.playerDeck.indexOf(cardId);

        if (idx !== -1) {
            nextRun.playerDeck.splice(idx, 1);
            nextRun.eventState.remaining -= 1;
            nextRun.eventState.removedCount += 1;

            if (nextRun.eventState.removedCount === 5) {
              nextRun.matchedCount >= 5 ? nextRun.consciousnessBonus += 7 : nextRun.consciousnessBonus += 3;
            }

            result = { success: true, remaining: nextRun.eventState.remaining };
            saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
            return nextRun;
        }

        result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.CARD_NOT_IN_DECK') };
        return prev;
    });
    return result;
  }, []);

  // D. 外交: 交渉価値システム
  const requestDiplomacyReroll = useCallback(() => {
    let result = { success: false };
    setActiveRun(prev => {
        if (!prev || prev.activeNode !== PROCEEDING_TYPES.DIPLOMACY) return prev;
        const totalCon = 12 + prev.consciousnessBonus;
        if (totalCon < 4) {
            result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.CON_INSUFFICIENT') };
            return prev;
        }
        const currentCount = (prev.eventState && Array.isArray(prev.eventState.presented)) ? prev.eventState.presented.length : 6;
        const countToGenerate = Math.max(1, currentCount);
        const pool = getNPCCardPool();
        const chosenCardIds = [];
        for (let i = 0; i < countToGenerate; i++) {
            if (pool.length > 0) {
                const card = pool[Math.floor(Math.random() * pool.length)];
                chosenCardIds.push(card.id);
            }
        }
        const nextRun = deepClone(prev);
        nextRun.consciousnessBonus -= 3;
        nextRun.eventState.presented = chosenCardIds;
        result = { success: true };
        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
    return result;
  }, [getNPCCardPool]);

  const executeDiplomacyExchange = useCallback((presentedCardId, playerCardIds) => {
    let result = { success: false };
    setActiveRun(prev => {
        if (!prev || prev.activeNode !== PROCEEDING_TYPES.DIPLOMACY) return prev;
        if (!Array.isArray(playerCardIds) || playerCardIds.length > 2) {
            result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.SELECT_MAX_2_CARDS') || 'Select up to 2 cards' };
            return prev;
        }

        const nextRun = deepClone(prev);

        const getCardValue = (id) => {
            if (id === 1000 || id === 33) return 3; // 芸術 or マネー
            const def = cardDefs[id];
            if (!def) return 1;
            if (def.required_scale < 8) return 1;
            if (def.required_scale < 30) return 2;
            return 3;
        };

        const targetValue = getCardValue(presentedCardId);
        let playerTotalValue = 0;

        let validRemoval = true;
        playerCardIds.forEach(id => {
            const idx = nextRun.playerDeck.indexOf(id);
            if (idx !== -1) {
                nextRun.playerDeck.splice(idx, 1);
                playerTotalValue += getCardValue(id);
            } else {
                validRemoval = false;
            }
        });

        if (!validRemoval) {
            result = { success: false, reason: getUILoc('YONOTSUNE.ERRORS.CARD_NOT_IN_DECK') };
            return prev;
        }

        addCardToDeck(nextRun, presentedCardId);

        // 提示カードリストから削除
        if (nextRun.eventState.presented) {
            const pIdx = nextRun.eventState.presented.indexOf(presentedCardId);
            if (pIdx !== -1) {
                nextRun.eventState.presented.splice(pIdx, 1);
            }
        }

        // 記憶効果「部族の闘争」なら交渉難航のみで決裂戦へ
        if (INITIAL_DECKS[nextRun.initialDeckKey]?.memoryEffect?.key === 'YONOTSUNE.MEMORY_EFFECTS.TRIBE') {
          playerTotalValue -= 1; // 交渉成立のハードルを上げる
        }

        let outcome = '';
        if (playerTotalValue > targetValue) {
            modifyScaleBonus(nextRun, 2);
            nextRun.consciousnessBonus += 2;
            nextRun.diplomacyWins += 1;
            outcome = 'success';  // 交渉成立

            if (nextRun.diplomacyWins >= 3) {
                unlockAchievement('DIPLOMACY_GENIUS');
            }
        } else if (playerTotalValue === targetValue) {
            outcome = 'compromise'; // 交渉妥結
        } else {
            // 記憶効果「部族の闘争」なら交渉難航のみで決裂戦へ
            if (INITIAL_DECKS[nextRun.initialDeckKey]?.memoryEffect?.key === 'YONOTSUNE.MEMORY_EFFECTS.TRIBE') {
              nextRun.isDiplomacyBreakdown = true;
              nextRun.activeNode = PROCEEDING_TYPES.FRICTION;

              result = { success: true, outcome: 'breakdown', detail: getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.RESULT_BREAKDOWN') };
              saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
              return nextRun;
            }
            else {
              // -5される直前の値が0未満なら決裂戦へ
              const beforePenalty = nextRun.scaleBonus;
              if (beforePenalty < 0) {
                nextRun.isDiplomacyBreakdown = true;
                nextRun.activeNode = PROCEEDING_TYPES.FRICTION;

                result = { success: true, outcome: 'breakdown', detail: getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.RESULT_BREAKDOWN') };
                saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
                return nextRun;
              }
              modifyScaleBonus(nextRun, -5);
            }
            outcome = 'penalty';  // 交渉難航
        }

        // 下限値補正
        if (nextRun.scaleBonus < -5) nextRun.scaleBonus = -5;
        if (nextRun.consciousnessBonus < -11) nextRun.consciousnessBonus = -11;

        nextRun.eventState.completed = true;
        result = { success: true, outcome, detail: outcome === 'success' ? getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.RESULT_SUCCESS') : outcome === 'compromise' ? getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.RESULT_COMPROMISE') : getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.RESULT_PENALTY') };
        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
    return result;
  }, [cardDefs, unlockAchievement, addCardToDeck, modifyScaleBonus]);

  // E. 対話の秋: 3つの選択肢
  const executeOpportunityRemove = useCallback((cardIds) => {
    // setActiveRunのコールバック外側で処理を行う（Strict Modeの二重実行を回避）
    const currentRun = getFromStorage(LOCAL_STORAGE_KEY)?.currentRun;
    if (!currentRun || currentRun.activeNode !== PROCEEDING_TYPES.OPPORTUNITY) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.INVALID_STATE') };
    }
    // 既に完了している場合はスキップ
    if (currentRun.eventState?.completed) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.ALREADY_COMPLETED') };
    }
    if (!Array.isArray(cardIds) || cardIds.length === 0 || cardIds.length > 3) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.SELECT_1_3_CARDS') };
    }

    const nextRun = deepClone(currentRun);

    const getCardValue = (id) => {
      if (id === 1000 || id === 33) return 3;
      const def = cardDefs[id];
      if (!def) return 1;
      if (def.required_scale < 8) return 1;
      if (def.required_scale < 30) return 2;
      return 3;
    };

    let totalVal = 0;
    let validRemoval = true;
    cardIds.forEach(id => {
      const idx = nextRun.playerDeck.indexOf(id);
      if (idx !== -1) {
        nextRun.playerDeck.splice(idx, 1);
        totalVal += getCardValue(id);
      } else {
        validRemoval = false;
      }
    });

    if (!validRemoval) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.CARD_NOT_IN_DECK') };
    }

    nextRun.consciousnessBonus += totalVal;
    nextRun.eventState.completed = true;

    saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
    setActiveRun(nextRun);

    if (goToNextNodeSelection) goToNextNodeSelection();

    return { success: true };
  }, [cardDefs, goToNextNodeSelection]);

  const executeOpportunityDraw = useCallback(() => {
    // setActiveRunのコールバック外側で処理を行う（Strict Modeの二重実行を回避）
    const currentRun = getFromStorage(LOCAL_STORAGE_KEY)?.currentRun;
    if (!currentRun || currentRun.activeNode !== PROCEEDING_TYPES.OPPORTUNITY) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.INVALID_STATE') };
    }
    // 既に完了している場合はスキップ
    if (currentRun.eventState?.completed) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.ALREADY_COMPLETED') };
    }
    const totalCon = 12 + currentRun.consciousnessBonus;
    if (totalCon < 4) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.CON_INSUFFICIENT') };
    }
    const pool = getNPCCardPool();
    if (pool.length === 0) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.POOL_EMPTY') };
    }

    const nextRun = deepClone(currentRun);
    nextRun.consciousnessBonus -= 3;
    const gainedCardIds = [];
    for (let i = 0; i < 3; i++) {
      const card = pool[Math.floor(Math.random() * pool.length)];
      addCardToDeck(nextRun, card.id);
      gainedCardIds.push(card.id);
    }
    nextRun.eventState.completed = true;

    saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
    setActiveRun(nextRun);

    return { success: true, gainedCardIds };
  }, [getNPCCardPool, addCardToDeck]);

  const startOpportunityBattle = useCallback(() => {
    // setActiveRunのコールバック外側で処理を行う（Strict Modeの二重実行を回避）
    const currentRun = getFromStorage(LOCAL_STORAGE_KEY)?.currentRun;
    if (!currentRun || currentRun.activeNode !== PROCEEDING_TYPES.OPPORTUNITY) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.INVALID_STATE') };
    }
    // 既に完了している場合はスキップ
    if (currentRun.eventState?.completed) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.ALREADY_COMPLETED') };
    }
    const totalCon = 12 + currentRun.consciousnessBonus;
    if (totalCon < 6) {
      return { success: false, reason: getUILoc('YONOTSUNE.ERRORS.CON_INSUFFICIENT') };
    }

    const nextRun = deepClone(currentRun);
    nextRun.consciousnessBonus -= 5;
    nextRun.isOpportunityBattle = true;
    nextRun.activeNode = PROCEEDING_TYPES.FRICTION;

    saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
    setActiveRun(nextRun);

    return { success: true };
  }, []);

  const clearOpportunityBattle = useCallback(() => {
    setActiveRun(prev => {
      if (!prev) return null;
      const nextRun = deepClone(prev);
      nextRun.isOpportunityBattle = false;
      saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
      return nextRun;
    });
  }, []);

  // 報酬の一括適用 (状態更新のレースコンディション防止)
  const applyBattleRewards = useCallback((patternKey, draftOptions, buffOption, isVictory) => {
    setActiveRun(prev => {
        if (!prev) return null;
        const nextRun = deepClone(prev);

        // 「文化摩擦」勝利時の記憶効果
        if (INITIAL_DECKS[nextRun.initialDeckKey]?.memoryEffect?.type === 'MATCH_WIN') {
          nextRun.consciousnessBonus += INITIAL_DECKS[nextRun.initialDeckKey].memoryEffect.con || 0;
          modifyScaleBonus(nextRun, INITIAL_DECKS[nextRun.initialDeckKey].memoryEffect.scale || 0);
        }

        // ドラフトの適用
        if (patternKey && draftOptions) {
            const cardIdsToAdd = patternKey === 'pattern1' ? draftOptions.pattern1 : draftOptions.pattern2;
            cardIdsToAdd.forEach(id => addCardToDeck(nextRun, id));
        }

        // バフの適用
        if (buffOption) {
            const { con, scale: sc, addCardId } = buffOption.effect;
            nextRun.consciousnessBonus += con;
            modifyScaleBonus(nextRun, sc);

            if (nextRun.consciousnessBonus < -11) nextRun.consciousnessBonus = -11;

            if (addCardId) {
                addCardToDeck(nextRun, addCardId);
            }
        }

        // 対戦終了処理（進行数カウントアップなど）
        if (nextRun.isDiplomacyBreakdown) {
            nextRun.isDiplomacyBreakdown = false;
            unlockAchievement('DIPLOMACY_FIGHTER');
        }

        if (nextRun.isOpportunityBattle) {
            nextRun.isOpportunityBattle = false;
            if (isVictory) {
                unlockAchievement('CLEAR_OPPORTUNITY_BATTLE');
            }
        }

        if (isVictory) {
            nextRun.isVictory = true;
            
            // 文化摩擦勝利（フェーズ6のボス戦も加算する）
            nextRun.frictionWins = (nextRun.frictionWins || 0) + 1;

            if (nextRun.currentPhase === 6) {
              const deckKey = nextRun.initialDeckKey;
              if (deckKey === 'AGRI') unlockAchievement('CLEAR_AGRI');
              else if (deckKey === 'TRIBE') unlockAchievement('CLEAR_TRIBE');
              else if (deckKey === 'UTOPIA') unlockAchievement('CLEAR_UTOPIA');
              else if (deckKey === 'TREASURE') unlockAchievement('CLEAR_TREASURE');

              const bossKey = nextRun.bossKey;
              if (bossKey === 'EMPIRE') unlockAchievement('DEFEAT_EMPIRE');
              else if (bossKey === 'MONOTHEISM') unlockAchievement('DEFEAT_MONOTHEISM');
              else if (bossKey === 'COMMUNITY') unlockAchievement('DEFEAT_COMMUNITY');
            }

            if (nextRun.playerDeck.includes(1000)) unlockAchievement('ART_SAVIOR');
        }

        // 報酬適用済みなのでリザルトデータをクリア
        nextRun.pendingMatchResult = null;

        saveToStorage(LOCAL_STORAGE_KEY, { isActive: true, currentRun: nextRun });
        return nextRun;
    });
  }, [unlockAchievement, addCardToDeck, modifyScaleBonus]);

  const generateEnemyDeck = useCallback((currentPhase, bossKey, isBreakdown, isOpportunity) => {
    if (isOpportunity) return { ...OPPORTUNITY_DECK, name: getUILoc(OPPORTUNITY_DECK.nameKey) };
    if (isBreakdown) return { ...DIPLOMACY_BREAKDOWN_DECK, name: getUILoc(DIPLOMACY_BREAKDOWN_DECK.nameKey) };
    // フェーズ6はボス戦
    if (currentPhase === 6) {
      const boss = BOSS_DECKS[bossKey] || BOSS_DECKS.EMPIRE;
      return { ...boss, name: getUILoc(boss.nameKey) };
    }

    // 1. ボーナスの動的分配 (Total = Phase ** 2)
    const totalBonusPoints = (currentPhase + 1) ** 2;
    // 規模への分配率を 0.3 ~ 0.7 の間でランダムに決定
    const scaleRatio = 0.3 + (Math.random() * 0.4);
    const scaleBonus = Math.floor(totalBonusPoints * scaleRatio);
    const conBonus = totalBonusPoints - scaleBonus;

    // 2. 有効規模に基づいたカードプールの制限
    const npcBaseScale = 5;
    const effectiveScale = npcBaseScale + scaleBonus;
    const maxAllowedRequiredScale = effectiveScale + 2;

    const fullPool = getNPCCardPool();
    if (fullPool.length === 0) return { name: getUILoc('YONOTSUNE.EVENTS.FRICTION.ENEMY_DECK'), cards: [7, 1], consciousness: 0, scale: 0 };

    // 制限プール（必要規模が maxAllowedRequiredScale 以下のもの）
    const restrictedPool = fullPool.filter(def => (def.required_scale || 0) <= maxAllowedRequiredScale);
    // もし制限プールが空になってしまった場合のセーフティ
    const activePool = restrictedPool.length > 0 ? restrictedPool : fullPool;

    // 3. デッキ構成の決定
    let typeCount = 2;
    let cardCount = 8;
    if (currentPhase === 1) { typeCount = 2; cardCount = 8; }
    else if (currentPhase === 2) { typeCount = 3; cardCount = 12; }
    else if (currentPhase === 3) { typeCount = 4; cardCount = 16; }
    else if (currentPhase === 4) { typeCount = 5; cardCount = 24; }
    else if (currentPhase === 5) { typeCount = 6; cardCount = 30; }

    // 制限プールから種類数分選ぶ
    const chosenCardIds = [];
    const tempPool = [...activePool];
    for (let i = 0; i < typeCount; i++) {
      if (tempPool.length > 0) {
        const idx = Math.floor(Math.random() * tempPool.length);
        chosenCardIds.push(tempPool.splice(idx, 1)[0].id);
      }
    }

    // デッキを埋める
    const cards = [];
    for (let i = 0; i < cardCount; i++) {
      cards.push(chosenCardIds[i % chosenCardIds.length]);
    }

    // 4. ワイルドカード（例外）の挿入
    // 全カードプール(fullPool)からランダムに2枚選び、デッキ内のランダムな位置のカードと入れ替える
    for (let i = 0; i < 2; i++) {
        if (fullPool.length > 0) {
            const wildCard = fullPool[Math.floor(Math.random() * fullPool.length)];
            const replaceIdx = Math.floor(Math.random() * cards.length);
            cards[replaceIdx] = wildCard.id;
        }
    }

    return {
      name: `${getUILoc('YONOTSUNE.EVENTS.FRICTION.ENEMY_SIDE')} (${currentPhase})`,
      cards,
      consciousness: conBonus,
      scale: scaleBonus
    };
  }, [getNPCCardPool]);

  // ランの終了
  const finishRun = useCallback(() => {
    // recordsの更新をsetActiveRunの外側で行う（Strict Modeの二重実行を回避）
    const currentRecords = getFromStorage(RECORDS_STORAGE_KEY) || DEFAULT_RECORDS;
    let updatedRecords = null;

    setActiveRun(prev => {
      if (prev) {
        // フェーズ6かつ勝利時のみクリアとカウント
        if (prev.currentPhase === 6 && prev.isVictory) {
          const memoryKey = prev.initialDeckKey || 'AGRI';
          const clearsByMemory = { ...(currentRecords.clearsByMemory || {}) };
          clearsByMemory[memoryKey] = (clearsByMemory[memoryKey] || 0) + 1;
          updatedRecords = { ...currentRecords, clearsByMemory };
        } else {
          const phase = prev.currentPhase || 1;
          const phaseLossCounts = { ...(currentRecords.phaseLossCounts || {}) };
          phaseLossCounts[phase] = (phaseLossCounts[phase] || 0) + 1;
          updatedRecords = { ...currentRecords, phaseLossCounts };
        }
      }
      return null;
    });

    // setActiveRunの外側で一度だけ保存
    if (updatedRecords) {
      saveToStorage(RECORDS_STORAGE_KEY, updatedRecords);
      setRecords(updatedRecords);
    }

    saveToStorage(LOCAL_STORAGE_KEY, {
      isActive: false,
      currentRun: null
    });
  }, []);

  return {
    activeRun,
    records,
    achievements,
    toast,
    startNewRun,
    selectNode,
    goToNextNodeSelection,
    dismissBossReveal,
    resetRun,
    resetAchievements,
    finishRun,
    executeWhimExchange,
    executeCrystal,
    hesitateCrystal,
    executeSharpenRemove,
    executeDiplomacyExchange,
    requestDiplomacyReroll,
    executeOpportunityRemove,
    executeOpportunityDraw,
    startOpportunityBattle,
    clearOpportunityBattle,
    generateDraftOptions,
    applyBattleRewards,
    generateBuffOptions,
    recordMatchResult,
    exportAchievements,
    importAchievements,
    unlockAchievement,
    generateEnemyDeck,
    cardDefs
  };
};

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
